// Source unique de vérité des sociétés (marques) gérées par eGesparc.
// Consolide ce qui était auparavant dispersé entre le switch hostname du module
// Vuex `domain` (src/store/index.ts) et la palette PrimeVue (src/layout/composables/societePalette.ts).
//
// Le `label` est l'identifiant canonique : c'est lui qui est stocké en localStorage,
// envoyé dans le header `x-domain` à chaque requête, et utilisé pour piloter le branding.

import logoJugandAutos from "@/assets/media/logo_jugand_autos.png";
import logoBalleydier4x4 from "@/assets/media/logo_balleydier4x4.png";
import logoPolarisMontBlanc from "@/assets/media/logo_polaris_montblanc.png";

export interface SocieteConfig {
  label: string; // identifiant envoyé dans x-domain, ex. "JUGAND AUTOS"
  color: string; // couleur Tailwind, ex. "vermilion"
  colorText: string;
  vehiclesIndex: string; // index Typesense
  bitrixBaseUrl: string;
  logo: string; // import statique du PNG
  // hostname historique de l'app pour cette société (rétro-compat / résolution initiale)
  hostname: string;
}

export const SOCIETES: Record<string, SocieteConfig> = {
  "JUGAND AUTOS": {
    label: "JUGAND AUTOS",
    color: "vermilion",
    colorText: "black",
    vehiclesIndex: "gesparc_jugand_autos_vehicules",
    bitrixBaseUrl: "https://intranet.jugandautos.com",
    logo: logoJugandAutos,
    hostname: "egesparc.jugandautos.com",
  },
  "BALLEYDIER 4X4": {
    label: "BALLEYDIER 4X4",
    color: "lightning-yellow",
    colorText: "black",
    vehiclesIndex: "gesparc_balleydier_4x4_vehicules",
    bitrixBaseUrl: "https://intranetbitrix.com",
    logo: logoBalleydier4x4,
    hostname: "egesparc.balleydier4x4.com",
  },
  "AUTO EVASION": {
    label: "AUTO EVASION",
    color: "alizarin-crimson",
    colorText: "black",
    // partage l'index Typesense de Jugand Autos
    vehiclesIndex: "gesparc_jugand_autos_vehicules",
    bitrixBaseUrl: "https://intranet.autoevasion04.fr",
    logo: "https://depot.jugandautos.com/documents/autoevasion.png",
    hostname: "egesparc.autoevasion04.fr",
  },
  "POLARIS MONT-BLANC": {
    label: "POLARIS MONT-BLANC",
    color: "congress-blue",
    colorText: "black",
    // partage l'index Typesense de Balleydier 4x4
    vehiclesIndex: "gesparc_balleydier_4x4_vehicules",
    bitrixBaseUrl: "https://intranet.polarismontblanc.com",
    logo: logoPolarisMontBlanc,
    hostname: "egesparc.polarismontblanc.com",
  },
  "ALPES DIFFUSION UTILITAIRES": {
    label: "ALPES DIFFUSION UTILITAIRES",
    color: "thunderbird",
    colorText: "black",
    // partage l'index Typesense de Balleydier 4x4
    vehiclesIndex: "gesparc_balleydier_4x4_vehicules",
    bitrixBaseUrl: "",
    logo: "https://depot.jugandautos.com/documents/ADU.png",
    hostname: "intranetbitrix.com",
  },
};

// Pages publiques (proposition `/p/`, bon de commande `/b/`) : servies par eGesparc lui-meme
// sur egesparc.com, pour toutes les societes — la page prend la palette de la societe
// emettrice. Elles vivaient auparavant sur le site de chaque marque (www.jugandautos.com,
// doc.balleydier4x4.com…). `www` directement : l'apex n'est qu'une redirection vers lui.
// Routeur en hash : le chemin passe apres `#`.
export const DOCUMENTS_PUBLICS_BASE_URL = "https://www.egesparc.com/#";

// Connexion « S'identifier avec Bitrix24 » : un seul portail, intranetbitrix.com,
// quelle que soit la société sélectionnée. `societe` est envoyée en `x-domain` à
// l'échange du code (`/check`) : l'API en déduit le portail sur lequel échanger le
// code et le champ du compte Bitrix qui porte la ref vendeur Gesparc.
export const BITRIX_CONNEXION = {
  domain: "intranetbitrix.com/oauth",
  clientId: "local.65733a9da3e5a0.16757212",
  societe: "BALLEYDIER 4X4",
};

export const SOCIETE_LABELS = Object.keys(SOCIETES);

export const DEFAULT_SOCIETE = "JUGAND AUTOS";

export function isSocieteLabel(value: unknown): value is string {
  return typeof value === "string" && value in SOCIETES;
}

// Résolution rétro-compatible d'un hostname vers un libellé société.
// Tolérante : on reconnaît la marque par sous-chaîne, quel que soit le TLD
// (egesparc.autoevasion.com, egesparc.autoevasion04.fr, …).
export function societeFromHostname(
  hostname: string | null | undefined
): string | null {
  if (!hostname) return null;
  const lower = hostname.toLowerCase();
  if (lower.includes("balleydier")) return "BALLEYDIER 4X4";
  if (lower.includes("polaris")) return "POLARIS MONT-BLANC";
  if (lower.includes("autoevasion") || lower.includes("evasion"))
    return "AUTO EVASION";
  if (lower.includes("jugand")) return "JUGAND AUTOS";
  return null;
}

// Détermine la société active au démarrage, par ordre de priorité :
// 1. valeur persistée en localStorage ; 2. société de l'utilisateur connecté ;
// 3. hostname courant ; 4. société par défaut.
export function resolveInitialSociete(
  stored: string | null | undefined,
  userSociete: string | null | undefined,
  hostname: string | null | undefined
): string {
  if (isSocieteLabel(stored)) return stored;
  if (isSocieteLabel(userSociete)) return userSociete;
  const fromHost = societeFromHostname(hostname);
  if (fromHost) return fromHost;
  return DEFAULT_SOCIETE;
}
